// src/scenes/expense/CreateExpense.jsx
import { useMemo, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";

/* ---------- axios ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? ""
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "select":
        rule = yup.string();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- page ---------- */
export default function CreateExpense() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  const FIELDS = useMemo(
    () => [
      {
        name: "name",
        label: "Expense Name",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "e.g. Office Supplies" },
      },
      {
        name: "amount",
        label: "Amount",
        type: "number",
        span: 2,
        required: true,
        min: 0,
       props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "expenseDate",
        label: "Expense Date",
        type: "date",
        span: 2,
        required: true,
        props: { InputLabelProps: { shrink: true } },
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        span: 4,
        required: false,
        props: { placeholder: "Optional details about the expense" },
      },
    ],
    []
  );

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);
  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);

  const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
    const payload = {
      ...values,
      amount: Number(values.amount),
    };

    // Convert date to ISO format
    if (values.expenseDate) {
      const d = new Date(values.expenseDate);
      if (!isNaN(d.getTime())) payload.expenseDate = d.toISOString();
    } else {
      delete payload.expenseDate;
    }

    const { data, error } = await postJSON("/api/expense", payload);
    setSubmitting(false);

    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) setErrors(error);
      setToast({ open: true, msg: "Failed to create expense", sev: "error" });
      return;
    }

    const msg = data?.message || "Expense created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();
  };

  return (
    <>
      <SimpleForm
        title="CREATE EXPENSE"
        subtitle="Add a new expense record"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Expense"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
